import { ReactElement, useCallback, useMemo } from 'react'
import { find, last, maxBy } from 'lodash-es'
import { v4 as uuid } from 'uuid'
import { Stack, Typography } from '@mui/material'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { useEditElementStore, useModelStore } from '@editorStores'
import { useModelClickListeners, useElementType } from '@editorHooks'
import { useStructuralPlanningFormEsc } from '@structuralPlanningHooks'
import { DEFAULT_LINE_LIKE_SETTINGS } from '../PlanSettingsForm/utils'
import { WallRipFormBase } from './components/WallRipFormBase'

export function createDefaultRipForWall(wall: ShapeObject): Rip {
  const p0 = wall.shape.points[0]
  const p1 = wall.shape.points[1]
  const lastPoint = last(wall.shape.points) as ImmutableVector3
  const wallZ = p0.z
  const highestZ = (maxBy(wall.shape.points, 'z') as ImmutableVector3)?.z ?? wallZ
  const midX = (p0.x + p1.x) / 2
  const midY = (p0.y + p1.y) / 2
  const start = new ImmutableVector3(midX, midY, wallZ)
  const end = new ImmutableVector3(midX, midY, highestZ)
  const xDirection = wall.shape.points[0].directionTo(lastPoint, true)
  const yDirection = wall.shape.points[0].directionTo(wall.shape.points[1], true)
  const zDirection = xDirection.cross(yDirection).normalize()
  return {
    ...DEFAULT_LINE_LIKE_SETTINGS,
    wall_guid: wall.guid,
    position_guid: '',
    domain_guid: uuid(),
    start,
    end,
    coordinate_system: {
      origin: start,
      x_direction: xDirection,
      y_direction: yDirection,
      z_direction: zDirection,
      TOLERANCE: 0,
    },
    element_type: 'rips',
    relative_position: '0.5',
    rip_type: 'AdditionalRip',
    storey: wall.storey,
  }
}

interface Props {
  /** Selected element: wall guid or rip position_guid */
  selectedElement?: string
  onClose: () => void
}

const WallRipForm = ({ selectedElement, onClose }: Props): ReactElement => {
  const setActiveElement = useEditElementStore(state => state.setActiveElement)

  const { model, removeRip } = useModelStore(state => state)

  const selectedRip = useMemo(
    () => (selectedElement ? find(model.rips, { position_guid: selectedElement }) : undefined),
    [model.rips, selectedElement],
  )
  const selectedWall = useMemo(
    () =>
      (selectedElement ? find(model.walls, { guid: selectedElement }) : undefined) as
        | ShapeObject
        | undefined,
    [model.walls, selectedElement],
  )

  const selectedElementType = useElementType(selectedElement)

  const handleWallClickSelectOnly = useCallback(
    (event: { object: { name: string } }) => {
      const guid = event.object.name
      const wall = find(model.walls, { guid })
      // `setActiveElement` keeps the current mode (adopt: 'keep'); `setElements` would adopt the
      // wall's own owning mode ('inner_walls'/'outer_walls'), kicking the drawer out of
      // 'wall-rip' and rendering the regular wall form instead of the rip form.
      if (wall) setActiveElement(guid)
    },
    [model.walls, setActiveElement],
  )
  useModelClickListeners(handleWallClickSelectOnly, [], true)

  useStructuralPlanningFormEsc(() => {
    if (selectedRip?.is_local) {
      removeRip(selectedRip.position_guid)
    }
    onClose()
  }, !!selectedElement)

  if (selectedElement && selectedElementType === 'rips') {
    return (
      <Stack direction="column" spacing={2}>
        <WallRipFormBase positionGuid={selectedElement} />
      </Stack>
    )
  }

  if (
    selectedWall &&
    (selectedElementType === 'inner_walls' || selectedElementType === 'outer_walls')
  ) {
    return (
      <Stack direction="column" spacing={2} p={1}>
        <WallRipFormBase key={selectedWall.guid} wallGuid={selectedWall.guid} isNewRip />
      </Stack>
    )
  }

  return (
    <Stack direction="column" spacing={1} alignItems="center" justifyContent="center" p={1}>
      <Typography>Wählen Sie eine Wand aus, um eine Rippe einzuzeichnen.</Typography>
    </Stack>
  )
}

export default WallRipForm
